import { Users, Eye, Target, TrendingUp } from 'lucide-react';

interface Props {
  visitors: number;
  pageviews: number;
  leads: number;
  conversionRate: number;
  /** Klicks auf Telefonnummer/E-Mail — Anrufer hinterlassen keine Daten und sind deshalb keine Formular-Leads. */
  contactClicks?: number;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

export default function StatCards({ visitors, pageviews, leads, conversionRate, contactClicks }: Props) {
  // LEITZAHL: Formulare + Telefon-/Mail-Klicks in EINER Zahl. Getrennte
  // Karten („Leads: 1" neben „Kontakt-Klicks: 23") lasen sich wie ein Fehler —
  // der Kunde will auf den ersten Blick sehen, wie viele Menschen Kontakt
  // aufgenommen haben. Die ehrliche Aufschlüsselung steht als Unterzeile dabei.
  const klicks = contactClicks ?? 0;
  const kontaktanfragen = leads + klicks;
  const cards = [
    {
      label: 'Besucher',
      value: fmt(visitors),
      icon: Users,
      color: 'text-blue-600 bg-blue-50',
      hint: 'Eindeutige Besucher (Summe der Tageswerte)',
    },
    {
      label: 'Seitenaufrufe',
      value: fmt(pageviews),
      icon: Eye,
      color: 'text-cyan-600 bg-cyan-50',
      hint: 'Gesamtzahl aufgerufener Seiten',
    },
    {
      label: 'Kontaktanfragen',
      value: fmt(kontaktanfragen),
      sub: `${fmt(leads)} über Formular · ${fmt(klicks)} Tel./Mail-Klicks`,
      icon: Target,
      color: 'text-green-600 bg-green-50',
      hint: 'Alle Kontaktaufnahmen: Formular-Anfragen plus Klicks auf Telefonnummer oder E-Mail-Adresse. Wer anruft, hinterlässt keine Daten — deshalb steht er nicht in der Lead-Liste, zählt hier aber mit.',
    },
    {
      label: 'Konversionsrate',
      value: `${(conversionRate * 100).toFixed(1)} %`,
      icon: TrendingUp,
      color: 'text-violet-600 bg-violet-50',
      hint: 'Kontaktanfragen ÷ Besucher',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-lg border border-gray-200 p-4" title={c.hint}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{c.label}</span>
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.color}`}>
              <c.icon className="w-4 h-4" />
            </span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{c.value}</p>
          {'sub' in c && c.sub ? <p className="mt-1 text-xs text-gray-500">{c.sub}</p> : null}
        </div>
      ))}
    </div>
  );
}
